import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Trophy } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Field, inputCls, textareaCls, selectCls, btnPrimary, btnGhost } from "../components/admin/uiKit";
import ImageUpload from "../components/admin/ImageUpload";
import { useSiteSettings } from "../context/SiteSettingsContext";

const DISCIPLINES = [
  "MMA", "Boxe", "Kickboxing", "Muay-thaï", "Lutte", "BJJ", "Judo", "Karaté", "Taekwondo",
];

const EMPTY = {
  first_name: "", last_name: "", nickname: "",
  gender: "M", birth_date: "",
  nationality: "BF", city: "",
  phone: "", whatsapp: "", email: "", instagram: "",
  academy_name: "",
  weight_class_id: "", current_weight_kg: "",
  height_cm: "", reach_cm: "", stance: "Orthodoxe",
  years_experience: "",
  disciplines: [],
  amateur_record: "", pro_record: "",
  portrait_url: null, full_body_url: null,
  how_heard: "", additional_info: "",
  honeypot: "",
};

// Custom upload component tied to the PUBLIC /api/applications/upload endpoint
function PublicImageUpload({ value, onChange, label, testId }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/applications/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <span className="text-[10px] font-stats uppercase tracking-widest text-neutral-500">{label}</span>
      <div className="flex items-center gap-3 flex-wrap">
        {value ? (
          <div className="relative w-24 h-32 border border-neutral-800 bg-neutral-900 overflow-hidden shrink-0">
            <img src={process.env.REACT_APP_BACKEND_URL + value} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />
          </div>
        ) : (
          <div className="w-24 h-32 border border-dashed border-neutral-800 bg-neutral-900/50 flex items-center justify-center text-neutral-700 text-[9px] font-stats uppercase tracking-widest shrink-0">
            AUCUNE
          </div>
        )}
        <label className={`${btnGhost} cursor-pointer`}>
          {busy ? "UPLOAD…" : value ? "Remplacer" : "Uploader"}
          <input type="file" accept="image/*" className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])} disabled={busy}
            data-testid={`${testId}-input`} />
        </label>
        {value && (
          <button type="button" onClick={() => onChange(null)} className="text-xs text-neutral-500 hover:text-white uppercase tracking-widest">
            Retirer
          </button>
        )}
      </div>
      {err && <p className="text-[11px] text-burkina-red font-stats uppercase tracking-widest">{err}</p>}
    </div>
  );
}

const Section = ({ title, hint, children }) => (
  <section className="border-t border-neutral-900 pt-8 md:pt-10">
    <p className="text-[10px] font-stats uppercase tracking-[0.3em] text-gold">{title}</p>
    {hint && <p className="mt-1 text-neutral-500 text-xs">{hint}</p>}
    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </section>
);

export default function Rejoindre() {
  const { settings } = useSiteSettings();
  const [form, setForm] = useState(EMPTY);
  const [weightClasses, setWeightClasses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  React.useEffect(() => {
    api.get("/weight-classes").then(({ data }) => setWeightClasses(data || [])).catch(() => {});
  }, []);

  const toggleDiscipline = (d) => {
    const has = form.disciplines.find((x) => x.discipline === d);
    if (has) {
      setForm({ ...form, disciplines: form.disciplines.filter((x) => x.discipline !== d) });
    } else {
      setForm({ ...form, disciplines: [...form.disciplines, { discipline: d, years: 0 }] });
    }
  };

  const setDisciplineYears = (d, years) => {
    setForm({
      ...form,
      disciplines: form.disciplines.map((x) => x.discipline === d ? { ...x, years: Number(years) || 0 } : x),
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const payload = {
        ...form,
        current_weight_kg: form.current_weight_kg === "" ? null : Number(form.current_weight_kg),
        height_cm: form.height_cm === "" ? null : Number(form.height_cm),
        reach_cm: form.reach_cm === "" ? null : Number(form.reach_cm),
        years_experience: form.years_experience === "" ? null : Number(form.years_experience),
        birth_date: form.birth_date || null,
        weight_class_id: form.weight_class_id || null,
      };
      await api.post("/applications", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section className="max-w-3xl mx-auto px-4 md:px-8 py-24 md:py-32" data-testid="rejoindre-thanks">
        <CheckCircle2 className="text-gold" size={48} />
        <p className="mt-6 text-xs uppercase tracking-[0.3em] text-gold font-stats">
          Candidature reçue
        </p>
        <h1 className="mt-4 font-display font-black text-5xl md:text-7xl text-white leading-none">
          BIEN REÇU, {form.first_name || "COMBATTANT"}.
        </h1>
        <p className="mt-6 text-neutral-400 text-base md:text-lg max-w-2xl leading-relaxed">
          Notre équipe étudie chaque candidature avec attention.
          Vous serez recontacté par téléphone ou WhatsApp dès qu'une décision sera prise.
          En attendant, continuez à vous préparer.
        </p>
        <Link to="/" className={`${btnGhost} mt-10`} data-testid="rejoindre-back-home">
          Retour à l'accueil →
        </Link>
      </section>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8" data-testid="rejoindre-page">
      <div className="pt-14 md:pt-20 pb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats flex items-center gap-2">
          <Trophy size={14} /> {(settings?.rejoindre_subtitle && settings.rejoindre_subtitle.trim()) || "Candidatures"}
        </p>
        <h1
          className="mt-4 font-display font-black text-5xl md:text-8xl leading-[0.85] text-white break-words"
          data-testid="rejoindre-title"
        >
          {(settings?.rejoindre_title && settings.rejoindre_title.trim()) || "REJOINDRE LA BUFL"}
        </h1>
        <p className="mt-6 text-neutral-400 text-base md:text-lg max-w-2xl leading-relaxed" data-testid="rejoindre-intro">
          {(settings?.rejoindre_intro && settings.rejoindre_intro.trim()) ||
            "Vous pensez avoir votre place dans la cage ? Présentez votre profil à notre équipe. Chaque candidature est étudiée personnellement."}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-10 pb-24">
        {/* Honeypot — hidden field bots fill */}
        <input
          type="text"
          value={form.honeypot}
          onChange={(e) => setForm({ ...form, honeypot: e.target.value })}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[9999px] w-0 h-0 opacity-0 pointer-events-none"
        />

        <Section title="Identité">
          <Field label="Prénom *">
            <input className={inputCls} required value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              data-testid="app-first-name" />
          </Field>
          <Field label="Nom">
            <input className={inputCls} value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              data-testid="app-last-name" />
          </Field>
          <Field label="Surnom">
            <input className={inputCls} value={form.nickname}
              onChange={(e) => setForm({ ...form, nickname: e.target.value })} />
          </Field>
          <Field label="Genre">
            <select className={selectCls} value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}>
              <option value="M">Homme</option>
              <option value="F">Femme</option>
            </select>
          </Field>
          <Field label="Date de naissance">
            <input type="date" className={inputCls} value={form.birth_date}
              onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
          </Field>
          <Field label="Nationalité (code ISO2)">
            <input className={inputCls} value={form.nationality} maxLength={3}
              onChange={(e) => setForm({ ...form, nationality: e.target.value.toUpperCase() })} placeholder="BF" />
          </Field>
          <Field label="Ville" className="md:col-span-2">
            <input className={inputCls} value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </Field>
        </Section>

        <Section title="Contact" hint="Ces informations restent internes et ne sont jamais publiées.">
          <Field label="Téléphone">
            <input className={inputCls} value={form.phone} type="tel"
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="app-phone" />
          </Field>
          <Field label="WhatsApp">
            <input className={inputCls} value={form.whatsapp} type="tel"
              onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
          </Field>
          <Field label="Email">
            <input className={inputCls} value={form.email} type="email"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="app-email" />
          </Field>
          <Field label="Instagram">
            <input className={inputCls} value={form.instagram}
              onChange={(e) => setForm({ ...form, instagram: e.target.value })}
              placeholder="@username" />
          </Field>
        </Section>

        <Section title="Sport">
          <Field label="Académie / Club">
            <input className={inputCls} value={form.academy_name}
              onChange={(e) => setForm({ ...form, academy_name: e.target.value })} />
          </Field>
          <Field label="Catégorie visée">
            <select className={selectCls} value={form.weight_class_id}
              onChange={(e) => setForm({ ...form, weight_class_id: e.target.value })}>
              <option value="">— sélectionner —</option>
              {weightClasses.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Poids actuel (kg)">
            <input type="number" step="0.1" className={inputCls} value={form.current_weight_kg}
              onChange={(e) => setForm({ ...form, current_weight_kg: e.target.value })} />
          </Field>
          <Field label="Taille (cm)">
            <input type="number" className={inputCls} value={form.height_cm}
              onChange={(e) => setForm({ ...form, height_cm: e.target.value })} />
          </Field>
          <Field label="Allonge (cm)">
            <input type="number" className={inputCls} value={form.reach_cm}
              onChange={(e) => setForm({ ...form, reach_cm: e.target.value })} />
          </Field>
          <Field label="Stance">
            <select className={selectCls} value={form.stance}
              onChange={(e) => setForm({ ...form, stance: e.target.value })}>
              <option value="Orthodoxe">Orthodoxe</option>
              <option value="Southpaw">Southpaw</option>
              <option value="Switch">Switch</option>
            </select>
          </Field>
          <Field label="Années d'expérience">
            <input type="number" className={inputCls} value={form.years_experience}
              onChange={(e) => setForm({ ...form, years_experience: e.target.value })} />
          </Field>
        </Section>

        <Section title="Disciplines pratiquées" hint="Cochez celles que vous avez pratiquées et indiquez le nombre d'années.">
          <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-2">
            {DISCIPLINES.map((d) => {
              const cur = form.disciplines.find((x) => x.discipline === d);
              const active = !!cur;
              return (
                <div key={d} className={`border ${active ? "border-gold bg-neutral-950" : "border-neutral-900"} p-3`}>
                  <label className="flex items-center justify-between gap-2 cursor-pointer">
                    <span className="text-xs font-display uppercase tracking-widest text-white">{d}</span>
                    <input type="checkbox" checked={active} onChange={() => toggleDiscipline(d)}
                      data-testid={`discipline-${d.toLowerCase().replace(/[^a-z]/g, '')}`} />
                  </label>
                  {active && (
                    <div className="mt-2 flex items-center gap-2 text-[10px] font-stats uppercase tracking-widest text-neutral-500">
                      <span>Années :</span>
                      <input type="number" min={0} max={50} value={cur.years}
                        onChange={(e) => setDisciplineYears(d, e.target.value)}
                        className="w-14 h-7 bg-neutral-900 border border-neutral-800 px-2 text-xs text-white focus:border-gold outline-none" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        <Section title="Records">
          <Field label="Record MMA amateur (W-L-D)">
            <input className={inputCls} value={form.amateur_record}
              onChange={(e) => setForm({ ...form, amateur_record: e.target.value })}
              placeholder="ex. 5-2-0" />
          </Field>
          <Field label="Record MMA pro (W-L-D)">
            <input className={inputCls} value={form.pro_record}
              onChange={(e) => setForm({ ...form, pro_record: e.target.value })}
              placeholder="ex. 3-0-0" />
          </Field>
        </Section>

        <Section title="Photos" hint="Portrait (buste) + photo plein corps. Optimisées automatiquement.">
          <PublicImageUpload
            value={form.portrait_url}
            onChange={(u) => setForm({ ...form, portrait_url: u })}
            label="Portrait"
            testId="app-portrait"
          />
          <PublicImageUpload
            value={form.full_body_url}
            onChange={(u) => setForm({ ...form, full_body_url: u })}
            label="Plein corps"
            testId="app-fullbody"
          />
        </Section>

        <Section title="Informations complémentaires">
          <Field label="Comment avez-vous entendu parler de BUFL ?" className="md:col-span-2">
            <input className={inputCls} value={form.how_heard}
              onChange={(e) => setForm({ ...form, how_heard: e.target.value })}
              placeholder="Réseaux sociaux, ami, article…" />
          </Field>
          <Field label="Autre information à partager" className="md:col-span-2">
            <textarea rows={4} className={textareaCls} value={form.additional_info}
              onChange={(e) => setForm({ ...form, additional_info: e.target.value })}
              placeholder="Palmarès, motivations, objectifs, disponibilités…" />
          </Field>
        </Section>

        {err && (
          <p className="text-xs text-burkina-red font-stats uppercase tracking-widest border border-burkina-red/30 px-3 py-2">
            {err}
          </p>
        )}

        <div className="border-t border-neutral-900 pt-8 flex flex-col md:flex-row items-start md:items-center gap-4">
          <button
            type="submit"
            disabled={submitting || !form.first_name}
            data-testid="app-submit"
            className={`${btnPrimary} h-12 px-6 disabled:opacity-50`}
          >
            {submitting ? "ENVOI EN COURS…" : "SOUMETTRE MA CANDIDATURE →"}
          </button>
          <p className="text-xs text-neutral-600 font-stats uppercase tracking-widest">
            Vos données personnelles restent internes et confidentielles.
          </p>
        </div>
      </form>
    </div>
  );
}
